import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import type { BuilderMessage, BuilderNode, BuilderTab, BuilderTemplate, BuilderTemplateId } from '../../../types/builder'
import { getBuilderTemplate, matchBuilderTemplate } from '../../../data/builderTemplates'
import { trackEvent } from '../../../utils/analytics'

let seq = 0
const uid = (prefix: string) => `${prefix}-${++seq}`
const cloneNode = (node: BuilderNode): BuilderNode => ({ ...node, id: uid(node.id), settings: node.settings.map((s) => ({ ...s })) })

const WELCOME: BuilderMessage = { id: 'welcome', role: 'assistant', text: 'What diligence process would you like to build?' }

function initialMessages(template: BuilderTemplate): BuilderMessage[] {
  if (template.id === 'blank') return [WELCOME, { id: uid('a'), role: 'assistant', text: template.reply }]
  return [WELCOME, { id: uid('u'), role: 'user', text: template.prompt }, { id: uid('a'), role: 'assistant', text: template.reply, plan: template.plan }]
}

/** Scripted state for the DGPT Builder preview. Nothing is sent to a model or saved. */
export function useBuilder(initial: BuilderTemplateId) {
  const reduceMotion = useReducedMotion()
  const [templateId, setTemplateId] = useState<BuilderTemplateId>(initial)
  const [messages, setMessages] = useState<BuilderMessage[]>(() => initialMessages(getBuilderTemplate(initial)))
  const [nodes, setNodes] = useState<BuilderNode[]>(() => getBuilderTemplate(initial).nodes.map(cloneNode))
  const [building, setBuilding] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<BuilderTab>('preview')
  const timers = useRef<number[]>([])

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  useEffect(() => clearTimers, [])

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms))
  }

  const run = useCallback(
    (template: BuilderTemplate, userText: string) => {
      clearTimers()
      setTemplateId(template.id)
      setSelectedId(null)
      setTab('preview')
      setNodes([])
      setBuilding(true)
      setMessages((current) => [...current, { id: uid('u'), role: 'user', text: userText }])

      const first = reduceMotion ? 0 : 600
      const step = reduceMotion ? 0 : 240
      later(() => setMessages((current) => [...current, { id: uid('a'), role: 'assistant', text: template.reply, plan: template.plan.length ? template.plan : undefined }]), first)
      template.nodes.forEach((n, index) => later(() => setNodes((current) => [...current, cloneNode(n)]), first + 300 + index * step))
      later(() => setBuilding(false), first + 300 + template.nodes.length * step)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reduceMotion],
  )

  const sendPrompt = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || building) return
    const id = matchBuilderTemplate(trimmed)
    trackEvent('builder_prompt_submit', { matched_template: id })
    const template = getBuilderTemplate(id)
    run(
      id === 'blank'
        ? { ...template, reply: 'This preview can’t build that process yet, so I’ve opened a blank workspace. Add agents from the Agent Library or try a suggested prompt.' }
        : template,
      trimmed,
    )
  }

  const loadTemplate = (id: BuilderTemplateId) => {
    trackEvent('builder_template_open', { template: id })
    const template = getBuilderTemplate(id)
    if (id === 'blank') return startBlank()
    run(template, template.prompt)
  }

  const startFromDgpt = () => {
    trackEvent('builder_template_open', { template: 'vc', source: 'dgpt_template' })
    run(getBuilderTemplate('vc'), 'Start from the DGPT venture capital workspace.')
  }

  function startBlank() {
    clearTimers()
    setTemplateId('blank')
    setNodes([])
    setSelectedId(null)
    setBuilding(false)
    setTab('preview')
    setMessages((current) => [
      ...current,
      { id: uid('u'), role: 'user', text: 'Start from a blank workspace.' },
      { id: uid('a'), role: 'assistant', text: getBuilderTemplate('blank').reply },
    ])
  }

  const addAgents = (names: string[]) => {
    if (!names.length) return
    trackEvent('builder_agents_added', { count: String(names.length) })
    setNodes((current) => [
      ...current,
      ...names.map<BuilderNode>((name) => ({
        id: uid('agent'),
        label: name,
        kind: 'agent',
        detail: `${name} agent`,
        settings: [
          { key: 'Agent', value: name },
          { key: 'Output', value: 'Structured findings with sources' },
          { key: 'Review', value: 'Analyst review required' },
        ],
      })),
    ])
    setMessages((current) => [
      ...current,
      { id: uid('a'), role: 'assistant', text: `Added ${names.length} agent${names.length > 1 ? 's' : ''} from the Agent Library: ${names.join(', ')}. Drag them into position on the canvas.` },
    ])
  }

  const selectNode = (id: string | null) => {
    setSelectedId(id)
    if (id) setTab('workflow')
  }

  const moveNode = (id: string, direction: -1 | 1) =>
    setNodes((current) => {
      const index = current.findIndex((n) => n.id === id)
      const target = index + direction
      if (index < 0 || target < 0 || target >= current.length) return current
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const removeNode = (id: string) => {
    setNodes((current) => current.filter((n) => n.id !== id))
    setSelectedId((current) => (current === id ? null : current))
  }

  const updateSetting = (id: string, key: string, value: string) =>
    setNodes((current) => current.map((n) => (n.id === id ? { ...n, settings: n.settings.map((s) => (s.key === key ? { ...s, value } : s)) } : n)))

  return {
    template: getBuilderTemplate(templateId),
    messages,
    nodes,
    setNodes,
    building,
    selectedNode: nodes.find((n) => n.id === selectedId) ?? null,
    selectNode,
    tab,
    setTab,
    sendPrompt,
    loadTemplate,
    startFromDgpt,
    startBlank,
    addAgents,
    moveNode,
    removeNode,
    updateSetting,
  }
}

export type BuilderController = ReturnType<typeof useBuilder>
